// src/pricer/services/PricerOrderProvider.jsx
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { flashItems, stopItems } from './pricer.service'
import { usePricerOptions } from './usePricerOptions'
import useFeature from '../../components/Shared/FeatureProvider/useFeature'
import useStateRef from '../../helpers/useStateRef'
import barcoder from 'barcoder'
import { sendTroubleshootingInfo } from '../../services/Troubleshooting/troubleshooting.service'

const PricerOrderContext = createContext(null)

const getNextEans = (orderItems, count, excludeId= null) =>
  orderItems
    .filter(oi => oi.id !== excludeId && !oi.pickingMethod && !oi.fulfillmentIssue && oi.object?.barcode)
    .slice(0, count)
    .map(oi => oi.object.barcode)
    .filter(ean => barcoder.validate(ean));

const PricerOrderProvider = ({ order, orderItems, children }) => {
  const [options] = usePricerOptions()
  const { isEnabled } = useFeature()
  const [currentlyBlinking, setCurrentlyBlinking, currentlyBlinkingRef] = useStateRef([]) // To keep track of currently blinking EANs without causing re-renders

  const isEnabledFeature = isEnabled('integration.pricer')

  const isActive = Boolean(window.Android) && isEnabledFeature && options.enabled && options.serverAddress


  const stopCurrent = useCallback(() => {
    if (!isActive || !currentlyBlinkingRef.current.length || !options.serverAddress) return
    stopItems(options.serverAddress, options.port, options.auth_key, currentlyBlinkingRef.current)
    currentlyBlinkingRef.current = []
    setCurrentlyBlinking([])
  }, [isActive, options.serverAddress, options.port, options.auth_key])

  const flash = useCallback(async (eans) => {
    if (!isActive || !eans.length || !options.serverAddress) return
    stopCurrent()
    currentlyBlinkingRef.current = eans
    setCurrentlyBlinking(eans)
    const isSuccess = await flashItems(options.serverAddress, options.port, options.auth_key, eans)

    if (!isSuccess) {
      await sendTroubleshootingInfo({
        error: 'Flash call failed',
        details: {
          action: 'flashItems',
          serverAddress: options.serverAddress,
          port: options.port,
          auth_key: options.auth_key,
          eans,
        },
      }, 'PRICER_FLASH_ERROR')
    }
  }, [isActive, options.serverAddress, options.port, options.auth_key])


  useEffect(() => {
    if (!isActive || !['PREPARING','ACCEPTED'].includes(order.status) || options.triggerMode === 0) return
    const count = options.triggerMode;
    flash(getNextEans(orderItems, count))
  }, [order.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const onItemFound = useCallback((foundItem) => {
    if (!isActive || options.triggerMode === 0) return
    stopCurrent()
    const count = options.triggerMode;
    flash(getNextEans(orderItems, count,foundItem.id))
  }, [isActive, options.triggerMode, orderItems, stopCurrent, flash]);

  const isBlinking = useCallback((ean) => {
    if (!isActive || !ean) return false
    return currentlyBlinking.includes(ean)
  }, [isActive,currentlyBlinking])

  return (
    <PricerOrderContext.Provider value={{ isActive,onItemFound, flash, isBlinking }}>
      {children}
    </PricerOrderContext.Provider>
  )
}

const usePricerOrder = () => useContext(PricerOrderContext)

export { PricerOrderProvider, usePricerOrder }
